import React, { useContext } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { getAiRolePlay } from 'redux/selectors/timeline';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

import { Button } from 'react-bootstrap';
import NvIcon from 'shared/components/nv-icon';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '../../../../../config/pendo.config.json';
import AiConversationContext, { AIRolePlayStatus, RolePlayInteractionMode } from './context';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import RolePlayTitle from './role-play-title';
import RecordingStatus from './voice-mode/recording-status';

const styles = css`
  .practice-again-button {
    height: ${doubleSpacing}px;
  }
`;

const ConversationModalHeader = () => {
  const dispatch = useDispatch();
  const {
    aiRolePlayStatus,
    aiRolePlayId,
    handleExitChat,
    isRolePlayStarting,
    handleStartConversation,
    interactionMode,
    voiceRecordingState,
  } = useContext(AiConversationContext);

  const { isMobile } = useAiRolePlayHelper();
  const aiRolePlay = useSelector((state) => getAiRolePlay(state, aiRolePlayId));
  const { title } = aiRolePlay || {};

  const handleStartOver = () => {
    dispatch(openConfirmationDialog({
      title: t.FORM.ARE_YOU_SURE(),
      bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER_CONFIRMATION.MESSAGE(),
      confirmText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER_CONFIRMATION.CONFIRM(),
      cancelText: t.FORM.CANCEL(),
      onConfirm: () => {
        handleStartConversation();
      },
    }));
  };

  return (
    <div css={styles} className={`border-bottom d-flex ${isMobile ? 'px-4' : 'px-6'}`}>
      <RolePlayTitle title={title} />
      {interactionMode === RolePlayInteractionMode.VOICE_MODE && voiceRecordingState != null && (
        <div className='mx-auto my-auto d-flex align-items-center'>
          <RecordingStatus recordingState={voiceRecordingState} />
        </div>
      )}
      {aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS && (
        <div className='ml-auto my-auto d-flex py-4'>
          <Button
            className='bg-transparent shadow-none border-0 text-gray-1 d-flex px-0'
            onClick={handleStartOver}
            disabled={isRolePlayStarting}
            aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER()}
            data-qa={config.pendo.activities.aiRolePlay.conversationModal.startOver}
          >
            <NvIcon
              icon='refresh'
              size='small'
              className='d-flex align-items-center'
            />
            {!isMobile && (
              <p className='mb-0 ml-2'>
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_OVER()}
              </p>
            )}
          </Button>
          <Button
            variant='danger'
            className={isMobile ? 'ml-4' : 'ml-5'}
            onClick={() => handleExitChat()}
            aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT()}
            data-qa={config.pendo.activities.aiRolePlay.conversationModal.exitChat}
          >
            {isMobile
              ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT()
              : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT()}
          </Button>
        </div>
      )}
      {aiRolePlayStatus === AIRolePlayStatus.COMPLETED && (
        <div className='ml-auto d-flex my-auto py-4'>
          <Button
            variant='primary'
            onClick={() => handleExitChat()}
            data-qa={config.pendo.activities.aiRolePlay.conversationModal.close}
          >
            {t.FORM.CLOSE()}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConversationModalHeader;
